/** @file Parses a chord string (`Mod+Shift+KeyZ`) into modifier flags and a physical key code. */
import type { Chord, ParsedChord, Platform } from './types';

const MODIFIER_FLAGS: Record<string, keyof Omit<ParsedChord, 'code'>> = { Ctrl: 'ctrl', Meta: 'meta', Shift: 'shift', Alt: 'alt', Mod: 'mod' };

function applyModifier(out: ParsedChord, part: string, platform: Platform): boolean {
  const flag = MODIFIER_FLAGS[part];
  if (!flag) return false;
  out[flag] = true;
  if (flag === 'mod') out[platform === 'mac' ? 'meta' : 'ctrl'] = true;
  return true;
}

/** Throws on malformed chords (no key code, unknown modifier, duplicate parts). `Mod` resolves per platform. */
export function parseKeys(chord: Chord, platform: Platform): ParsedChord {
  const out: ParsedChord = { code: '', mod: false, ctrl: false, meta: false, shift: false, alt: false };
  const seen = new Set<string>();
  for (const part of chord.split('+').map((p) => p.trim())) {
    if (part === '' || seen.has(part)) throw new Error(`invalid chord "${chord}"`);
    seen.add(part);
    if (applyModifier(out, part, platform)) continue;
    if (out.code !== '') throw new Error(`invalid chord "${chord}": two key codes`);
    out.code = part;
  }
  if (out.code === '') throw new Error(`invalid chord "${chord}": no key code`);
  return out;
}
